<script setup lang="ts">
    import { InputRow, Combobox, Select } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { computed } from 'vue';
    import type { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { Alert } from '@/components/ui/alert';

    const { t } = useI18n();

    const props = defineProps<{
        form: any;
    }>();

    const typeOptions = computed<SelectOption[]>(() => {
        return [
            { value: 'percentage', label: 'employee.pension.type_percentage' },
            { value: 'amount', label: 'employee.pension.type_option' },
        ];
    });

    const pensionCompanyOptions = computed<SelectOption[]>(() => {
        return [
            {
                value: t('employee.pension.pension_other'),
                label: t('employee.pension.pension_other'),
            },
            { value: 'AP', label: 'AP' },
            { value: 'AkademikerPension', label: 'AkademikerPension' },
            { value: 'Alm. Brand', label: 'Alm. Brand' },
            { value: 'Arkitekternes Pensionskasse', label: 'Arkitekternes Pensionskasse' },
            { value: 'Bankpension', label: 'Bankpension' },
            { value: 'Børne- og Ungdomspædagoger', label: 'Børne- og Ungdomspædagoger' },
            { value: 'Danica', label: 'Danica' },
            { value: 'Dansk Erhverv', label: 'Dansk Erhverv' },
            {
                value: 'Danske Civil - og Akademiingeniører',
                label: 'Danske Civil - og Akademiingeniører',
            },
            { value: 'Funktionærpension', label: 'Funktionærpension' },
            {
                value: 'Jordbrugsakademikere og Dyrlæger',
                label: 'Jordbrugsakademikere og Dyrlæger',
            },
            { value: 'Jurist og økonom', label: 'Jurist og økonom' },
            { value: 'Industriens Pension', label: 'Industriens Pension' },
            { value: 'Letpension', label: 'Letpension' },
            { value: 'Lægernes Pensionskasse', label: 'Lægernes Pensionskasse' },
            { value: 'Lærernes Pension', label: 'Lærernes Pension' },
            { value: 'Nordnet Bank', label: 'Nordnet Bank' },
            { value: 'Nordea Pension', label: 'Nordea Pension' },
            { value: 'P+(Djøf)', label: 'P+(Djøf)' },
            { value: 'P+(IDA)', label: 'P+(IDA)' },
            { value: 'PFA', label: 'PFA' },
            { value: 'PKA', label: 'PKA' },
            { value: 'PKA Plus Pension', label: 'PKA Plus Pension' },
            { value: 'PMF Pension', label: 'PMF Pension' },
            { value: 'Pen-Sam Liv', label: 'Pen-Sam Liv' },
            { value: 'PensionDanmark', label: 'PensionDanmark' },
            { value: 'SEB Pension', label: 'SEB Pension' },
            {
                value: 'Sampension (tidl. Grafisk Pension)',
                label: 'Sampension (tidl. Grafisk Pension)',
            },
            { value: 'Sampension (tidl. KP)', label: 'Sampension (tidl. KP)' },
            { value: 'Sampension (tidl. StK:Pension)', label: 'Sampension (tidl. StK:Pension)' },
            { value: 'Sampension vedr. tjenestemænd', label: 'Sampension vedr. tjenestemænd' },
            { value: 'Saxo Bank', label: 'Saxo Bank' },
            { value: 'Skandia Link', label: 'Skandia Link' },
            { value: 'Skandia Liv', label: 'Skandia Liv' },
            { value: 'Spar Lolland', label: 'Spar Lolland' },
            { value: 'Teknikum- Og Diplomingeniør', label: 'Teknikum- Og Diplomingeniør' },
            { value: 'Topdanmark', label: 'Topdanmark' },
            { value: 'Velliv', label: 'Velliv' },
        ];
    });

    const valueType = computed(() => {
        return props.form.companyPension.type === 'amount' ? 'kr.' : '%';
    });
</script>

<template>
    <div class="grid gap-3">
        <InputRow
            :form="form"
            name="type"
            label="employee.pension.type"
            error-key="companyPension.type"
        >
            <Select
                id="type"
                v-model="form.companyPension.type"
                :options="typeOptions"
                :placeholder="t('employee.pension.type')"
                :show-remove-button="false"
                translate
            />
        </InputRow>

        <Alert
            v-if="form.companyPension.type === 'amount'"
            variant="warning"
        >
            {{ t('employee.pension.amount_warning') }}
        </Alert>

        <InputRow
            :form="form"
            name="employee_contribution"
            label="employee.pension.employee_contribution"
            error-key="companyPension.employee_contribution"
        >
            <div class="relative">
                <Input
                    id="employee_contribution"
                    v-model="form.companyPension.employee_contribution"
                    :placeholder="t('employee.pension.employee_contribution')"
                    type="number"
                    step="any"
                />
                <span
                    class="text-muted-foreground pointer-events-none absolute top-0 right-2.5 text-xs leading-7.5"
                    >{{ valueType }}</span
                >
            </div>
        </InputRow>

        <InputRow
            :form="form"
            name="company_contribution"
            label="employee.pension.company_contribution"
            error-key="companyPension.company_contribution"
        >
            <div class="relative">
                <Input
                    id="company_contribution"
                    v-model="form.companyPension.company_contribution"
                    :placeholder="t('employee.pension.company_contribution')"
                    type="number"
                    step="any"
                />
                <span
                    class="text-muted-foreground pointer-events-none absolute top-0 right-2.5 text-xs leading-7.5"
                >
                    {{ valueType }}
                </span>
            </div>
        </InputRow>

        <InputRow
            :form="form"
            name="pension_company"
            label="employee.pension.pension_company"
            error-key="companyPension.pension_company"
        >
            <Combobox
                id="pension_company"
                v-model="form.companyPension.pension_company"
                :options="pensionCompanyOptions"
                :placeholder="t('employee.pension.select_pension_company')"
                :show-remove-button="false"
            />
        </InputRow>
    </div>
</template>
